import React, { useEffect, useId, useState, useRef } from 'react'
import Member from 'components/teams/Member'
import useStore from 'state/knovStore'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import api from '../../api/api'
import cn from 'classnames'
import styles from 'components/teams/team-settings.module.scss'
import WithTooltip from 'components/shared/WithTooltip'
import { isMobile } from 'lib/device'
import TreechatTooltip from 'components/shared/TreechatTooltip'
import { showTooltipConfirmation } from 'state/imperativeApis/tooltipApi'
import TeamLogo from 'components/shared/icons/TeamLogo'
import SettingsHeader from 'components/shared/settings/SettingsHeader'
import Button from 'components/shared/buttons/Button'
import { PurpleButton } from 'components/shared/buttons/Button'
import CopyTooltip from 'components/shared/CopyTooltip'
import {
    themedSettingsSelectStyles,
    themedUserInviteSelectStyles,
} from 'components/shared/reactSelectStyles'
import { panelSwipeExclusionProps } from 'components/shared/panelSwipeExclusions'
import { buildStreamInviteUrl } from 'components/teams/streamInviteUrl'
import { insertToClipboard } from 'utils'
import queryClient from 'api/queryClient'
import { getQuestsQueryKey } from 'refactor/hooks/api/useGetQuests'
import { withDepth } from 'components/filters/useStreamFilters'
import TeamBannerCropper from './TeamBannerCropper'
import TeamLogoCropper from './TeamLogoCropper'
import useUserHasFeature from 'refactor/hooks/useUserHasFeature'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import isGifFile from 'utils/isGifFile'

const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

const privateStreamPermissionValue = value => {
    const permission = value || 'anyone'
    return permission === 'anyone' ? 'members' : permission
}

const isPrivateStreamTeam = team => team?.public !== true

const PRIVATE_STREAM_PERMISSION_OPTIONS = [
    { value: 'admin', label: 'Admins only' },
    { value: 'members', label: 'Members' },
]

const PUBLIC_STREAM_PERMISSION_OPTIONS = [
    ...PRIVATE_STREAM_PERMISSION_OPTIONS,
    { value: 'anyone', label: 'Anyone' },
]

const VISIBILITY_OPTIONS = [
    { value: 'public', label: 'Public' },
    { value: 'private', label: 'Private' },
]

const HOST_REVENUE_OPTIONS = [
    { value: 'on', label: 'On' },
    { value: 'off', label: 'Off' },
]

const THREAD_DISPLAY_OPTIONS = [
    { value: 'default', label: 'Roots & Upvalues' },
    { value: 'roots_only', label: 'Root threads only' },
    { value: 'all_branches', label: 'All branches' },
]

const SEEDING_MODE_OPTIONS = [
    { value: 'upvalue_only', label: 'Upvalues only' },
    { value: 'user_choice', label: 'Members choose' },
    { value: 'force_seed', label: 'All upvalues are seeds' },
]

const findSelectOption = (options, value) =>
    options.find(option => option.value === value) || options[0]

const teamSettingsInviteSelectStyles = {
    ...themedUserInviteSelectStyles,
    menuPortal: base => ({
        ...base,
        zIndex: 1701,
    }),
}

// react-select paints its control inline, so the money accent has to be
// composed onto the shared settings styles rather than set in CSS.
const hostRevenueSelectStyles = {
    ...themedSettingsSelectStyles,
    control: (base, state) => {
        const themedControl = themedSettingsSelectStyles.control(base, state)
        return {
            ...themedControl,
            border: '1px solid color-mix(in srgb, var(--host-money) 55%, transparent)',
            ':hover': {
                ...themedControl[':hover'],
                borderColor: 'var(--host-money)',
            },
        }
    },
    singleValue: base => ({
        ...themedSettingsSelectStyles.singleValue(base),
        color: 'var(--host-money-text)',
        fontWeight: 700,
    }),
    dropdownIndicator: base => ({
        ...themedSettingsSelectStyles.dropdownIndicator(base),
        color: 'var(--host-money-text)',
    }),
}

export default function Team(props) {
    const seedingEnabled = useUserHasFeature('seeding')
    const [team, setTeam] = useState(props.team)
    const closeModal = props.closeModal
    const activeSpaceId = useStore(state => state.activeSpaceId)
    const userStreams = useStore(state => state.userStreams)

    useEffect(
        function getTeam() {
            // Always fetch the full team data to get members and invitations
            const teamId = props.teamId || props.team?.id
            if (teamId) {
                setTeamLoading(true)
                api.getTeam(teamId)
                    .then(team => {
                        setTeam(team)
                        setTeamName(team.name)
                        setBannerLoading(true)
                        setTeamLoading(false)
                    })
                    .catch(error => {
                        console.error('Error fetching team:', error)
                        setTeamLoading(false)
                    })
            }
        },
        [props.teamId, props.team?.id],
    )

    const [selectedOptions, setSelectedOptions] = useState([])
    const [inviteError, setInviteError] = useState(null)
    const [teamName, setTeamName] = useState(props.team?.name)
    const [showBannerCropper, setShowBannerCropper] = useState(false)
    const [selectedBannerImage, setSelectedBannerImage] = useState(null)
    const [bannerKey, setBannerKey] = useState(Date.now())
    const [bannerLoading, setBannerLoading] = useState(!!props.team?.id)
    const [bannerError, setBannerError] = useState(false)
    const [teamLoading, setTeamLoading] = useState(true)
    const [isLeavingStream, setIsLeavingStream] = useState(false)
    const [leaveStreamError, setLeaveStreamError] = useState(null)
    const [descriptionDraft, setDescriptionDraft] = useState(props.team?.description || '')
    const [showLogoCropper, setShowLogoCropper] = useState(false)
    const [selectedLogoImage, setSelectedLogoImage] = useState(null)
    const [logoKey, setLogoKey] = useState(Date.now())
    const bannerInputRef = useRef(null)
    const logoInputRef = useRef(null)
    const visibilitySelectId = useId()
    const hostRevenueSelectId = useId()
    // The host half of the program: may the caller host, and at what rate
    // (host_grants#mine). The stream half is team.host_revenue_enabled, set per
    // stream right here.
    const [hostContext, setHostContext] = useState(null)
    const { useUsd } = useBsvDisplay({ enabled: false })
    const postPermissionSelectId = useId()
    const replyPermissionSelectId = useId()
    const threadDisplaySelectId = useId()
    const seedingModeSelectId = useId()

    const [users, setUsers] = useState(null)
    useEffect(
        function getUsers() {
            if (team)
                api.getUsers({ space_id: team.space_id }).then(users => {
                    setUsers(users)
                })
        },
        [team],
    )

    const handleChange = selectedOption => {
        setSelectedOptions(selectedOption)
    }

    const filterOption = (option, inputValue) => {
        const { value } = option
        return value.toLowerCase().startsWith(inputValue.toLowerCase())
    }

    const handleSubmit = e => {
        e.preventDefault()
        api.addToTeam(
            {
                emails: selectedOptions.filter(o => o?.__isNew__).map(o => o?.value),
                ids: selectedOptions
                    .filter(o => !o?.__isNew__)
                    .map(a => users.find(u => u?.name === a?.value)?.id),
            },
            team.id,
        ).then(data => {
            // Limit responses (too many invitees, daily cap) carry an error and no team:
            // keep the channel and the selection, and show the message.
            if (!data?.team) {
                setInviteError(data?.error || 'Could not send invites. Please try again.')
                return
            }
            setInviteError(null)
            setSelectedOptions(null)
            setTeam(data.team)
        })
    }

    const handleDeleteMember = async userId => {
        const apiTeam = await api.removeFromTeam(team.id, userId)
        setTeam(apiTeam)
    }

    const handleDeleteInvite = async email => {
        // @ts-expect-error deleteInvite exists on api but is not included in the exported type
        const apiTeam = await api.deleteInvite(email, team.id)
        if (apiTeam) {
            setTeam(apiTeam)
        }
    }

    const invalidateTeamQuestQueries = teamId => {
        const normalizedTeamId = String(teamId)
        const invalidateFilterKey = filterKey => {
            queryClient.invalidateQueries({
                queryKey: ['quests', filterKey],
                exact: true,
            })
        }

        if (props.filter?.team_ids?.some(id => String(id) === normalizedTeamId)) {
            invalidateFilterKey(getQuestsQueryKey(withDepth(props.filter))[1])
        }

        queryClient
            .getQueryCache()
            .getAll()
            .forEach(query => {
                const key = query.queryKey
                if (!Array.isArray(key) || key[0] !== 'quests' || typeof key[1] !== 'string') return

                try {
                    const parsed = JSON.parse(key[1])
                    if (
                        Array.isArray(parsed?.team_ids) &&
                        parsed.team_ids.some(teamId => String(teamId) === normalizedTeamId)
                    ) {
                        invalidateFilterKey(key[1])
                    }
                } catch (_error) {
                    // Ignore non-JSON or unrelated cache keys.
                }
            })
    }

    useEffect(() => {
        setDescriptionDraft(team?.description || '')
    }, [team?.id, team?.description])

    // Any channel owner can switch revenue share on, so only the stream's admin
    // needs the lookup.
    const currentUserId = gon.currentUser?.id
    const isTeamAdmin =
        currentUserId && team?.admin_id && String(currentUserId) === String(team.admin_id)
    useEffect(() => {
        if (!isTeamAdmin) return
        let cancelled = false
        ;(async () => {
            try {
                const data = await api.getMyHostGrant()
                if (!cancelled) setHostContext(data || null)
            } catch (e) {
                // A lookup failure just hides the section.
            }
        })()
        return () => {
            cancelled = true
        }
    }, [isTeamAdmin])

    const updateTeamSetting = async params => {
        if (!team?.id) return
        const updated = await api.updateTeam(team.id, params)
        if (updated?.id) {
            setTeam(updated)

            useStore.getState().set(state => {
                const replaceTeam = teams =>
                    (teams || []).map(existingTeam =>
                        existingTeam.id === updated.id
                            ? { ...existingTeam, ...updated }
                            : existingTeam,
                    )

                state.userStreams = replaceTeam(state.userStreams)
                state.publicTeams = replaceTeam(state.publicTeams)
            })

            invalidateTeamQuestQueries(updated.id)
        } else {
            console.error('Team update failed:', updated)
        }
    }

    const removeStreamMembershipFromClient = teamId => {
        const normalizedTeamId = String(teamId)
        useStore.getState().set(state => {
            state.userStreams = (state.userStreams || []).filter(
                stream => String(stream.id) !== normalizedTeamId,
            )
            if (state.pinnedTeamIds) delete state.pinnedTeamIds[normalizedTeamId]
        })

        if (gon.currentUser?.space_teams) {
            gon.currentUser.space_teams = gon.currentUser.space_teams.filter(
                stream => String(stream.id) !== normalizedTeamId,
            )
        }
    }

    const handleLeaveStream = async () => {
        if (!team?.id || isLeavingStream) return

        setLeaveStreamError(null)
        setIsLeavingStream(true)
        try {
            await api.leaveTeam(team.id)
            removeStreamMembershipFromClient(team.id)
            invalidateTeamQuestQueries(team.id)
            if (closeModal) {
                closeModal()
                return
            }
        } catch (error) {
            console.error('Error leaving stream:', error)
            setLeaveStreamError('Could not leave channel. Please try again.')
        }

        setIsLeavingStream(false)
    }

    const handleBannerSelect = async e => {
        const input = e.target
        const file = input.files[0]
        // Reset the input so the same file can be selected again
        input.value = ''
        if (file && file.size > 10 * 1024 * 1024) {
            // 10MB limit
            alert('File too large. Please select an image under 10MB.')
            return
        }

        if (file && (await isGifFile(file))) {
            // The cropper redraws through a canvas, which keeps only the first
            // frame. Upload GIFs as-is so they stay animated; banners render with
            // object-fit: cover, and the server skips variants for GIFs.
            setBannerLoading(true)
            handleBannerSave(new Blob([file], { type: 'image/gif' }), 'banner.gif')
        } else if (file) {
            const reader = new FileReader()
            reader.onload = e => {
                setSelectedBannerImage(e.target.result)
                setShowBannerCropper(true)
            }
            reader.readAsDataURL(file)
        }
    }

    const handleBannerSave = async (bannerBlob, filename = 'banner.png') => {
        const formData = new FormData()
        formData.append('banner_image', bannerBlob, filename)

        try {
            await api.updateTeamBanner(team.id, formData)
            // Refetch the team to get all data including the new banner
            const freshTeam = await api.getTeam(team.id)
            setTeam(freshTeam)
            setShowBannerCropper(false)

            // Force re-render of banner image
            setBannerKey(Date.now())
            setBannerLoading(true)
            setBannerError(false)

            // Force refresh by updating the team in the store
            try {
                // The SearchHeader will refresh when the modal closes
            } catch (err) {
                console.log('Could not refresh streams:', err)
            }
        } catch (error) {
            console.error('Error updating banner:', error)
            setBannerLoading(false)
        }
    }

    const handleLogoSelect = e => {
        const file = e.target.files[0]
        if (file && file.size > 5 * 1024 * 1024) {
            // 5MB limit for logos
            alert('File too large. Please select an image under 5MB.')
            e.target.value = '' // Reset input
            return
        }

        if (file) {
            const reader = new FileReader()
            reader.onload = e => {
                setSelectedLogoImage(e.target.result)
                setShowLogoCropper(true)
            }
            reader.readAsDataURL(file)
        }
        // Reset the input so the same file can be selected again
        e.target.value = ''
    }

    const handleLogoSave = async croppedBlob => {
        const formData = new FormData()
        formData.append('team_image', croppedBlob, 'logo.png')

        try {
            const updatedTeam = await api.updateTeam(team.id, formData)
            setTeam(updatedTeam)
            setShowLogoCropper(false)

            // Force re-render of logo
            setLogoKey(Date.now())

            // Update the team in the store
            const teams = await api.getTeams({ space_id: activeSpaceId })
            useStore.getState().set({ userStreams: teams })
        } catch (error) {
            console.error('Error updating logo:', error)
        }
    }

    const memberListComponent = team?.users?.map(user => {
        return (
            <Member
                key={user.id}
                shouldHandleDelete={gon.currentUser.id == team.admin_id && user.id != team.admin_id}
                handleDelete={() => handleDeleteMember(user.id)}
                content={`@${user.name}`}
                className={styles.streamMemberPill}
            />
        )
    })

    const pendingEmailInvites = team?.invitations || []
    const pendingUserInvites = team?.pending_users || []
    const pendingInviteCount = pendingEmailInvites.length + pendingUserInvites.length
    const invitedUserIds = new Set(
        [...(team?.users || []), ...pendingUserInvites].map(user => user?.id),
    )
    const invitationListComponent = [
        ...pendingEmailInvites.map(invitation => (
            <Member
                key={`email-${invitation.id}`}
                content={invitation.email}
                shouldHandleDelete={true}
                handleDelete={() => handleDeleteInvite(invitation.email)}
                className={styles.streamMemberPill}
            />
        )),
        ...pendingUserInvites.map(user => (
            <Member
                key={`user-${user.id}`}
                content={`@${user.name}`}
                shouldHandleDelete={true}
                handleDelete={() => handleDeleteInvite(user.email)}
                className={styles.streamMemberPill}
            />
        )),
    ]

    const showSaveBtn = teamName !== team?.name
    // Mirrors the server (teams_controller#requested_host_revenue_switch): the
    // stream's owner may switch it on when HostGrant.can_host?, and off always.
    const canSetHostRevenue =
        !!isTeamAdmin && (!!hostContext?.can_host || !!team?.host_revenue_enabled)
    const hostSharePercent =
        hostContext?.grant?.share_percent ?? hostContext?.default_share_percent ?? 10
    // A channel left switched on after Treechat stopped this host's payouts: it can
    // still be switched off here, but it earns nothing.
    const hostPayoutsStopped =
        !!team?.host_revenue_enabled && !!hostContext && !hostContext.can_host
    const isCurrentUserMember =
        !!isTeamAdmin ||
        team?.users?.some(user => String(user.id) === String(currentUserId)) ||
        (userStreams || []).some(stream => String(stream.id) === String(team?.id)) ||
        (gon.currentUser?.space_teams || []).some(stream => String(stream.id) === String(team?.id))
    const canLeaveStream = !!team?.id && !!isCurrentUserMember && !isTeamAdmin
    const isPrivateStream = isPrivateStreamTeam(team)
    const visibilityBadge = team ? (
        <div className={styles.visibilityBadge}>
            <i className={cn('fa', team.public ? 'fa-globe' : 'fa-lock')} />
            <span>{team.public ? 'Public' : 'Private'}</span>
        </div>
    ) : null
    const visibilityMeta = team ? (
        <div className={styles.visibilityMeta}>
            <i className={cn('fa', team.public ? 'fa-globe' : 'fa-lock')} />
            <span>{team.public ? 'Public' : 'Private'}</span>
        </div>
    ) : null
    const postPermissionValue = isPrivateStream
        ? privateStreamPermissionValue(team?.who_can_post_threads)
        : team?.who_can_post_threads || 'anyone'
    const replyPermissionValue = isPrivateStream
        ? privateStreamPermissionValue(team?.who_can_reply_threads)
        : team?.who_can_reply_threads || 'anyone'
    const selectMenuPortalTarget = typeof document === 'undefined' ? undefined : document.body
    const streamPermissionOptions = isPrivateStream
        ? PRIVATE_STREAM_PERMISSION_OPTIONS
        : PUBLIC_STREAM_PERMISSION_OPTIONS
    const handleReactSelectTeamSetting = settingName => async option => {
        if (!option) return
        await updateTeamSetting({ [settingName]: option.value })
    }

    // Private streams cannot offer "anyone" posting/replying, so switching to
    // private must coerce those settings in the same write — otherwise the stream
    // keeps a permission its own UI can no longer represent.
    const handleVisibilityChange = async option => {
        if (!option) return
        const nextPublic = option.value === 'public'
        if (nextPublic === !!team?.public) return

        const params = { public: nextPublic }
        if (!nextPublic) {
            if ((team?.who_can_post_threads || 'anyone') === 'anyone') {
                params.who_can_post_threads = 'members'
            }
            if ((team?.who_can_reply_threads || 'anyone') === 'anyone') {
                params.who_can_reply_threads = 'members'
            }
        }
        await updateTeamSetting(params)
    }

    const handleHostRevenueChange = async option => {
        if (!option) return
        const next = option.value === 'on'
        if (next === !!team?.host_revenue_enabled) return

        await updateTeamSetting({ host_revenue_enabled: next })
    }

    if (teamLoading) {
        return (
            <div className={cn(styles.teamSettingsContainer, closeModal && styles.modalContainer)}>
                <div className={cn(styles.teamSettingsComp, styles.loadingContainer)}>
                    <i className="fa fa-spinner fa-spin fa-2x" />
                    <p>Loading channel settings...</p>
                </div>
            </div>
        )
    }

    if (team && !isTeamAdmin) {
        return (
            <div className={cn(styles.teamSettingsContainer, closeModal && styles.modalContainer)}>
                <div
                    className={cn(
                        'team-comp',
                        styles.teamSettingsComp,
                        styles.memberSettingsComp,
                        isMobile && styles.mobileTeamSettingsComp,
                        closeModal && styles.modalView,
                    )}
                >
                    <div className={styles.memberSettingsHeader}>
                        <TeamLogo
                            key={logoKey}
                            team={team}
                            contextStyles={styles.memberSettingsLogo}
                            initialStyles={cn(styles.initialStyles, styles.memberSettingsInitials)}
                        />
                        <div className={styles.memberSettingsHeaderContent}>
                            <div className={styles.memberSettingsLabel}>Channel Settings</div>
                            <div className={styles.memberSettingsNameRow}>
                                <div className={styles.memberSettingsName}>{team.name}</div>
                                {visibilityBadge}
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionCard}>
                        <h5 className={styles.sectionHeader}>Membership</h5>
                        <div className={styles.memberSettingsCopy}>
                            {canLeaveStream
                                ? 'You are a member of this channel.'
                                : 'You are not a member of this channel.'}
                        </div>
                        {leaveStreamError && (
                            <div className={styles.leaveStreamError}>{leaveStreamError}</div>
                        )}
                        <div className={styles.memberSettingsActions}>
                            <Button
                                type="default"
                                icon="fa fa-sign-out"
                                text="Leave Channel"
                                inProgress={isLeavingStream}
                                disabled={!canLeaveStream}
                                contextStyles={styles.leaveStreamButton}
                                onClick={handleLeaveStream}
                            />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={cn(styles.teamSettingsContainer, closeModal && styles.modalContainer)}>
            <div
                className={cn(
                    'team-comp',
                    styles.teamSettingsComp,
                    isMobile && styles.mobileTeamSettingsComp,
                    closeModal && styles.modalView,
                )}
                style={{ position: 'relative' }}
            >
                <div className={styles.settingsHeaderContainer}>
                    {team && (
                        <SettingsHeader
                            header="Channel Settings"
                            name={team?.name}
                            IconComponent={
                                team ? (
                                    <TeamLogo
                                        key={logoKey}
                                        team={team}
                                        contextStyles={styles.bigLogo}
                                        initialStyles={cn(styles.initialStyles, styles.bigInitials)}
                                    />
                                ) : null
                            }
                            openImageUpdateModal={() => logoInputRef.current.click()}
                            handleSave={async name => {
                                const apiTeam = await api.updateTeam(team.id, { name })
                                setTeam(apiTeam)
                            }}
                            nameAccessory={visibilityMeta}
                        />
                    )}
                </div>

                <div className={styles.sectionCard}>
                    <h5 className={styles.sectionHeader}>Description</h5>
                    <textarea
                        className={styles.descriptionInput}
                        value={descriptionDraft}
                        onChange={e => setDescriptionDraft(e.target.value)}
                        placeholder="Add a short description for this channel"
                        rows={3}
                        maxLength={1000}
                    />
                    {descriptionDraft !== (team?.description || '') && (
                        <div className={styles.descriptionActions}>
                            <Button
                                type="default"
                                text="Cancel"
                                onClick={() => setDescriptionDraft(team?.description || '')}
                            />
                            <Button
                                type="primary"
                                text="Save"
                                contextStyles={styles.descriptionSaveBtn}
                                onClick={() => updateTeamSetting({ description: descriptionDraft })}
                            />
                        </div>
                    )}
                </div>

                <div className={cn(styles.bannerSection, styles.sectionCard)}>
                    <h5 className={styles.sectionHeader}>Channel Banner</h5>
                    <div
                        className={styles.bannerPreview}
                        onClick={() => bannerInputRef.current.click()}
                    >
                        {team?.id ? (
                            <>
                                <img
                                    key={bannerKey}
                                    src={`${window.knovApiUrl}/api/v1/teams/${team.id}/banner?size=medium&t=${bannerKey}`}
                                    onError={e => {
                                        setBannerLoading(false)
                                        setBannerError(true)
                                        const target = /** @type {HTMLImageElement} */ (e.target)
                                        target.style.display = 'none'
                                    }}
                                    onLoad={e => {
                                        setBannerLoading(false)
                                        setBannerError(false)
                                        const target = /** @type {HTMLImageElement} */ (e.target)
                                        target.style.display = 'block'
                                    }}
                                    className={styles.bannerImage}
                                    style={{
                                        display: bannerLoading || bannerError ? 'none' : 'block',
                                    }}
                                />
                                {/* Always show skeleton while loading */}
                                {bannerLoading && <div className={styles.bannerSkeleton} />}
                                {/* Show placeholder on error or if no banner exists */}
                                {!bannerLoading && bannerError && (
                                    <div
                                        className={styles.bannerPlaceholder}
                                        style={{ display: 'flex' }}
                                    >
                                        <span>Click to upload banner</span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={styles.bannerPlaceholder} style={{ display: 'flex' }}>
                                <span>Click to upload banner</span>
                            </div>
                        )}
                        <div className={styles.editBannerButton}>
                            <i className="fa fa-pencil" />
                        </div>
                    </div>

                    <input
                        ref={bannerInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleBannerSelect}
                        style={{ display: 'none' }}
                    />
                </div>

                {canSetHostRevenue && (
                    <div
                        className={cn(
                            styles.sectionCard,
                            styles.hostRewardsCard,
                            useUsd ? styles.hostRewardsUsd : styles.hostRewardsSats,
                            team?.host_revenue_enabled && styles.hostRewardsOn,
                        )}
                    >
                        <h5 className={cn(styles.sectionHeader, styles.hostRewardsHeader)}>
                            Host Rewards
                            {!hostPayoutsStopped && (
                                <span className={styles.hostRewardsRate}>
                                    {hostSharePercent}% / upvalue
                                </span>
                            )}
                        </h5>
                        <div className={styles.permissionsContainer}>
                            <div className={styles.permissionRow} {...panelSwipeExclusionProps}>
                                <label
                                    className={styles.permissionLabel}
                                    htmlFor={hostRevenueSelectId}
                                >
                                    Earn a share of upvalues here?
                                </label>
                                <div className={styles.settingsSelect}>
                                    <Select
                                        inputId={hostRevenueSelectId}
                                        value={findSelectOption(
                                            HOST_REVENUE_OPTIONS,
                                            team?.host_revenue_enabled ? 'on' : 'off',
                                        )}
                                        onChange={handleHostRevenueChange}
                                        options={HOST_REVENUE_OPTIONS}
                                        styles={hostRevenueSelectStyles}
                                        menuPortalTarget={selectMenuPortalTarget}
                                        menuPosition="fixed"
                                        isSearchable={false}
                                        isClearable={false}
                                    />
                                </div>
                            </div>
                            <div className={styles.helperText}>
                                {hostPayoutsStopped ? (
                                    <>
                                        Host rewards are turned off for your account, so this
                                        channel no longer pays you a share of upvalues.
                                    </>
                                ) : team?.host_revenue_enabled ? (
                                    <>
                                        You earn {hostSharePercent}% of every upvalue members send
                                        to content in this channel, paid straight to your wallet. It
                                        comes out of Treechat&apos;s fee, so authors keep everything
                                        they earn.
                                    </>
                                ) : (
                                    <>
                                        Turn this on to earn {hostSharePercent}% of every upvalue
                                        members send to content in this channel. You can switch it
                                        on for any channel you own.
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.sectionCard}>
                    <h5 className={styles.sectionHeader}>Visibility</h5>
                    <div className={styles.permissionsContainer}>
                        <div className={styles.permissionRow} {...panelSwipeExclusionProps}>
                            <label className={styles.permissionLabel} htmlFor={visibilitySelectId}>
                                Who can see this channel?
                            </label>
                            <div className={styles.settingsSelect}>
                                <Select
                                    inputId={visibilitySelectId}
                                    value={findSelectOption(
                                        VISIBILITY_OPTIONS,
                                        team?.public ? 'public' : 'private',
                                    )}
                                    onChange={handleVisibilityChange}
                                    options={VISIBILITY_OPTIONS}
                                    styles={themedSettingsSelectStyles}
                                    menuPortalTarget={selectMenuPortalTarget}
                                    menuPosition="fixed"
                                    isSearchable={false}
                                    isClearable={false}
                                />
                            </div>
                        </div>
                        <div className={styles.helperText}>
                            {team?.public ? (
                                <>
                                    Anyone can read this channel and find it in discovery. Your
                                    invite link works for people who don&apos;t have an account yet.
                                </>
                            ) : (
                                <>
                                    Only members can read this channel. It stays out of discovery
                                    and search engines.
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.sectionCard}>
                    <h5 className={styles.sectionHeader}>Channel Posting Permissions</h5>

                    <div className={styles.permissionsContainer}>
                        <div className={styles.permissionRow} {...panelSwipeExclusionProps}>
                            <label
                                className={styles.permissionLabel}
                                htmlFor={postPermissionSelectId}
                            >
                                Who can create threads?
                            </label>
                            <div className={styles.settingsSelect}>
                                <Select
                                    inputId={postPermissionSelectId}
                                    value={findSelectOption(
                                        streamPermissionOptions,
                                        postPermissionValue,
                                    )}
                                    onChange={handleReactSelectTeamSetting('who_can_post_threads')}
                                    options={streamPermissionOptions}
                                    styles={themedSettingsSelectStyles}
                                    menuPortalTarget={selectMenuPortalTarget}
                                    menuPosition="fixed"
                                    isSearchable={false}
                                    isClearable={false}
                                />
                            </div>
                        </div>

                        <div className={styles.permissionRow} {...panelSwipeExclusionProps}>
                            <label
                                className={styles.permissionLabel}
                                htmlFor={replyPermissionSelectId}
                            >
                                Who can reply?
                            </label>
                            <div className={styles.settingsSelect}>
                                <Select
                                    inputId={replyPermissionSelectId}
                                    value={findSelectOption(
                                        streamPermissionOptions,
                                        replyPermissionValue,
                                    )}
                                    onChange={handleReactSelectTeamSetting('who_can_reply_threads')}
                                    options={streamPermissionOptions}
                                    styles={themedSettingsSelectStyles}
                                    menuPortalTarget={selectMenuPortalTarget}
                                    menuPosition="fixed"
                                    isSearchable={false}
                                    isClearable={false}
                                />
                            </div>
                        </div>

                        <div className={styles.helperText}>
                            {isPrivateStream ? (
                                <>
                                    Private channels allow <strong>Admins only</strong> or{' '}
                                    <strong>Members</strong>.
                                </>
                            ) : (
                                <>
                                    Defaults to <strong>Anyone</strong> for existing channels.
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.sectionCard}>
                    <h5 className={styles.sectionHeader}>Thread display</h5>
                    <div className={styles.permissionsContainer}>
                        <div className={styles.permissionRow} {...panelSwipeExclusionProps}>
                            <label
                                className={styles.permissionLabel}
                                htmlFor={threadDisplaySelectId}
                            >
                                Which thread branches appear in this channel?
                            </label>
                            <div className={styles.settingsSelect}>
                                <Select
                                    inputId={threadDisplaySelectId}
                                    value={findSelectOption(
                                        THREAD_DISPLAY_OPTIONS,
                                        team?.thread_display_mode || 'default',
                                    )}
                                    onChange={handleReactSelectTeamSetting('thread_display_mode')}
                                    options={THREAD_DISPLAY_OPTIONS}
                                    styles={themedSettingsSelectStyles}
                                    menuPortalTarget={selectMenuPortalTarget}
                                    menuPosition="fixed"
                                    isSearchable={false}
                                    isClearable={false}
                                />
                            </div>
                        </div>
                        <div className={styles.helperText}>
                            Root threads are brand new threads. Upvalued threads are branches from
                            messages that have been upvalued.
                        </div>
                    </div>
                </div>

                {seedingEnabled && (
                    <div className={styles.sectionCard}>
                        <h5 className={styles.sectionHeader}>Paid signals</h5>
                        <div className={styles.permissionsContainer}>
                            <div className={styles.permissionRow} {...panelSwipeExclusionProps}>
                                <label
                                    className={styles.permissionLabel}
                                    htmlFor={seedingModeSelectId}
                                >
                                    How should paid signals work in this channel?
                                </label>
                                <div className={styles.settingsSelect}>
                                    <Select
                                        inputId={seedingModeSelectId}
                                        value={findSelectOption(
                                            SEEDING_MODE_OPTIONS,
                                            team?.seeding_mode || 'upvalue_only',
                                        )}
                                        onChange={handleReactSelectTeamSetting('seeding_mode')}
                                        options={SEEDING_MODE_OPTIONS}
                                        styles={themedSettingsSelectStyles}
                                        menuPortalTarget={selectMenuPortalTarget}
                                        menuPosition="fixed"
                                        isSearchable={false}
                                        isClearable={false}
                                    />
                                </div>
                            </div>
                            <div className={styles.helperText}>
                                Seeds split half to the author and half among earlier seeders.
                            </div>
                        </div>
                    </div>
                )}

                <div className={cn(styles.standingInviteContainer, styles.sectionCard)}>
                    <h5 className={styles.sectionHeader}>Members &amp; Invites</h5>

                    <div className={styles.subSection}>
                        <div className={styles.subSectionHeader}>
                            Members
                            {(team?.users?.length || 0) > 0 && (
                                <span className={styles.sectionCount}>{team.users.length}</span>
                            )}
                        </div>
                        <div className="user-tag-container">{memberListComponent}</div>
                    </div>

                    <div className={styles.subSection}>
                        <div className={styles.subSectionHeader}>Invite a member</div>
                        <div className={styles.subHeader}>Type email or username then tab</div>
                        <form className="invite-form" onSubmit={handleSubmit}>
                            <div
                                className={styles.memberInviteSelect}
                                {...panelSwipeExclusionProps}
                            >
                                <CreatableSelect
                                    isMulti
                                    value={selectedOptions}
                                    onChange={handleChange}
                                    styles={teamSettingsInviteSelectStyles}
                                    menuPortalTarget={selectMenuPortalTarget}
                                    menuPosition="fixed"
                                    placeholder={
                                        users
                                            ? 'Enter a username or email address and hit tab...'
                                            : 'Loading...'
                                    }
                                    filterOption={filterOption}
                                    formatCreateLabel={v =>
                                        emailRegex.test(v)
                                            ? `Invite "${v}" by email`
                                            : 'Please enter a valid Treechat username or email address'
                                    }
                                    options={users
                                        ?.filter(u => !invitedUserIds.has(u?.id))
                                        .filter(u => u?.id != gon?.currentUser?.id)
                                        .map(u => ({ value: u?.name, label: u?.name }))}
                                />
                            </div>

                            <div className={styles.inviteBtnContainer}>
                                <button
                                    type="submit"
                                    style={{ border: 'none', background: 'none', padding: 0 }}
                                >
                                    <PurpleButton text="Invite" />
                                </button>
                            </div>
                            {inviteError && <div role="alert">{inviteError}</div>}
                        </form>
                    </div>

                    <div className={styles.subSection}>
                        <div className={styles.subSectionHeader}>
                            Outstanding invites
                            {pendingInviteCount > 0 && (
                                <span className={styles.sectionCount}>{pendingInviteCount}</span>
                            )}
                        </div>
                        {pendingInviteCount > 0 ? (
                            <div className="user-tag-container">{invitationListComponent}</div>
                        ) : (
                            <div className={styles.helperText}>No pending invites.</div>
                        )}
                    </div>

                    <div className={styles.subSection}>
                        <div className={styles.subSectionHeader}>Standing invite</div>
                        <div className={styles.inviteToggleRow}>
                            <div className={styles.permissionLabel}>Allow standing invite link</div>

                            <label className={styles.toggleSwitch}>
                                <input
                                    type="checkbox"
                                    checked={!!team?.standing_invitations_allowed}
                                    onChange={async e => {
                                        const value = e.target.checked
                                        await updateTeamSetting({
                                            standing_invitations_allowed: value,
                                        })
                                    }}
                                />
                                <span className={styles.toggleSlider}></span>
                            </label>
                        </div>
                        {team?.standing_invitations_allowed && (
                            <div className={styles.inviteLinkRow}>
                                <div style={{ fontWeight: 500 }}>Copy Invite Link</div>
                                <div className={styles.inviteBtnContainer} style={{ marginTop: 0 }}>
                                    {(() => {
                                        const tooltipText = 'Copy invite link'
                                        const tipped = React.createRef()
                                        const showConfirmationTooltip = (
                                            tipped,
                                            confirmationText = 'Copied!',
                                            tooltipText,
                                        ) => {
                                            showTooltipConfirmation(tipped.current, {
                                                content: confirmationText,
                                                restoreContent: tooltipText,
                                                duration: isMobile ? 1000 : 2000,
                                                closeAfter: isMobile,
                                            })
                                        }

                                        const onCopy = ev => {
                                            ev.preventDefault()
                                            ev.stopPropagation()
                                            const url = buildStreamInviteUrl(
                                                team.id,
                                                window.location.origin,
                                            )
                                            insertToClipboard(url)
                                            showConfirmationTooltip(tipped, 'Copied!', tooltipText)
                                        }

                                        return (
                                            <CopyTooltip tip={tooltipText} offset={0} ref={tipped}>
                                                <div
                                                    style={{
                                                        border: 'none',
                                                        background: 'none',
                                                        padding: 0,
                                                    }}
                                                    onClick={onCopy}
                                                >
                                                    <PurpleButton text="Copy Link" />
                                                </div>
                                            </CopyTooltip>
                                        )
                                    })()}
                                </div>
                            </div>
                        )}
                        <div className={styles.helperText}>
                            Anyone with this link can join after logging in.
                        </div>
                    </div>
                </div>

                {!props.closeModal && <TreechatTooltip />}
            </div>

            {showBannerCropper && (
                <TeamBannerCropper
                    image={selectedBannerImage}
                    onSave={handleBannerSave}
                    onCancel={() => setShowBannerCropper(false)}
                />
            )}

            {showLogoCropper && (
                <TeamLogoCropper
                    image={selectedLogoImage}
                    onSave={handleLogoSave}
                    onCancel={() => setShowLogoCropper(false)}
                />
            )}

            <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoSelect}
                style={{ display: 'none' }}
            />
        </div>
    )
}
